import * as ExcelJS from "exceljs";

const EMPTY_EXPORT_PLACEHOLDERS = new Set([
  "n/a",
  "na",
  "n.a.",
  "null",
  "undefined",
]);

/** Backend export headers we never want in the downloaded sheet. */
const STRIP_EXPORT_HEADERS = new Set(["comment status"]);

/** Headers treated as the comments/description column to place after images. */
const COMMENT_DESCRIPTION_HEADERS = new Set([
  "comment description",
  "comment_description",
  "comments",
  "التعليقات",
  "وصف التعليق",
]);

const COMMENT_DESCRIPTION_RENAME_FROM = new Set([
  "comment description",
  "comment_description",
  "وصف التعليق",
]);

const COMMENT_DESCRIPTION_COLUMN_WIDTH = 32;
const COMMENT_DESCRIPTION_HEADER_LABEL = "Comments";
const COMMENT_DESCRIPTION_HEADER_LABEL_AR = "التعليقات";

const normalizeEmptyExportCell = (value) => {
  if (value == null || value === "") return "-";
  if (
    typeof value === "string" &&
    EMPTY_EXPORT_PLACEHOLDERS.has(value.trim().toLowerCase())
  ) {
    return "-";
  }
  return value;
};

const cellValueToKeyPart = (value) => {
  if (value == null) return "";
  if (typeof value === "object") {
    if (typeof value.text === "string") return value.text;
    if (value.richText) {
      return value.richText.map((part) => part.text || "").join("");
    }
    if (value.result != null) return String(value.result);
    try {
      return JSON.stringify(value);
    } catch (_err) {
      return String(value);
    }
  }
  return String(value);
};

const normalizeHeaderLabel = (value) =>
  cellValueToKeyPart(value).trim().toLowerCase().replace(/\s+/g, " ");

/** Build a stable key for a worksheet data row (ExcelJS `row.values[0]` is unused). */
const rowValuesKey = (row) =>
  (row.values || [])
    .slice(1)
    .map((value) => cellValueToKeyPart(value))
    .join("\u0001");

/**
 * Drop duplicate data rows the API sometimes emits (e.g. one row per image/comment
 * join) when exported columns make those rows identical.
 */
const dedupeIdenticalDataRows = (worksheet) => {
  const seen = new Set();
  const duplicateRowNumbers = [];

  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return;
    const key = rowValuesKey(row);
    if (seen.has(key)) {
      duplicateRowNumbers.push(rowNumber);
      return;
    }
    seen.add(key);
  });

  for (let i = duplicateRowNumbers.length - 1; i >= 0; i -= 1) {
    worksheet.spliceRows(duplicateRowNumbers[i], 1);
  }
};

/** Remove unwanted backend columns (e.g. Comment Status) by header label. */
/** Remove unwanted backend columns (e.g. Comment Status) by header label. */
const stripUnwantedExportColumns = (worksheet) => {
  const headerRow = worksheet.getRow(1);
  if (!headerRow) return;

  const columnsToRemove = [];
  headerRow.eachCell({ includeEmpty: false }, (cell, colNumber) => {
    const label = normalizeHeaderLabel(cell.value);
    if (STRIP_EXPORT_HEADERS.has(label)) {
      columnsToRemove.push(colNumber);
    }
  });

  for (let i = columnsToRemove.length - 1; i >= 0; i -= 1) {
    const colNumber = columnsToRemove[i];
    worksheet.spliceColumns(colNumber, 1);
    // ExcelJS does not move floating images when splicing columns.
    shiftFloatingImagesLeft(worksheet, colNumber);
  }
};

/**
 * Shift worksheet image anchors left after a 1-based column is removed.
 * nativeCol is 0-based in ExcelJS anchors.
 */
const shiftFloatingImagesLeft = (worksheet, removedCol1Based) => {
  const removedNativeCol = removedCol1Based - 1;
  worksheet.getImages().forEach((image) => {
    if (!image?.range?.tl) return;
    [image.range.tl, image.range.br].forEach((anchor) => {
      if (!anchor || typeof anchor.nativeCol !== "number") return;
      if (anchor.nativeCol > removedNativeCol) {
        anchor.nativeCol -= 1;
      }
    });
  });
};

/**
 * Pack floating images into Image 1–4 header columns left-to-right per row.
 * Fixes empty Image 1 + untitled trailing image column after column strips.
 */
const realignExportImageAnchors = (worksheet) => {
  const headerRow = worksheet.getRow(1);
  if (!headerRow) return;

  const imageColByIndex = {};
  headerRow.eachCell({ includeEmpty: false }, (cell, colNumber) => {
    const match = /^image\s*([1-4])$/.exec(normalizeHeaderLabel(cell.value));
    if (match) {
      imageColByIndex[Number(match[1])] = colNumber;
    }
  });

  const orderedIndexes = [1, 2, 3, 4].filter((index) => imageColByIndex[index]);
  if (!orderedIndexes.length) return;

  const targetNativeCols = orderedIndexes.map(
    (index) => imageColByIndex[index] - 1,
  );
  const minNative = Math.min(...targetNativeCols);
  // Allow one overflow column past Image 4 (untitled image after a bad strip).
  const maxNative = Math.max(...targetNativeCols) + 1;

  const byRow = new Map();
  worksheet.getImages().forEach((image) => {
    if (!image?.range?.tl) return;
    const col = Math.floor(Number(image.range.tl.nativeCol));
    const row = Math.floor(Number(image.range.tl.nativeRow));
    if (Number.isNaN(col) || Number.isNaN(row)) return;
    if (col < minNative || col > maxNative) return;
    if (!byRow.has(row)) byRow.set(row, []);
    byRow.get(row).push(image);
  });

  byRow.forEach((rowImages) => {
    rowImages.sort(
      (a, b) =>
        Math.floor(a.range.tl.nativeCol) - Math.floor(b.range.tl.nativeCol),
    );
    rowImages.forEach((image, index) => {
      if (index >= targetNativeCols.length) return;
      const nextCol = targetNativeCols[index];
      const currentCol = Math.floor(image.range.tl.nativeCol);
      const colDelta = nextCol - currentCol;
      if (!colDelta) return;
      image.range.tl.nativeCol += colDelta;
      if (image.range.br && typeof image.range.br.nativeCol === "number") {
        image.range.br.nativeCol += colDelta;
      }
    });
  });
};

const findCommentDescriptionColumn = (worksheet) => {
  const headerRow = worksheet.getRow(1);
  if (!headerRow) return null;

  let commentCol = null;
  let preferredCol = null;
  headerRow.eachCell({ includeEmpty: false }, (cell, colNumber) => {
    const label = normalizeHeaderLabel(cell.value);
    if (COMMENT_DESCRIPTION_RENAME_FROM.has(label)) {
      preferredCol = colNumber;
      return;
    }
    if (COMMENT_DESCRIPTION_HEADERS.has(label) && commentCol == null) {
      commentCol = colNumber;
    }
  });
  return preferredCol || commentCol;
};

/** Rename backend `comment_description` / Comment Description header to Comments. */
const renameCommentDescriptionHeader = (worksheet) => {
  const headerRow = worksheet.getRow(1);
  if (!headerRow) return;

  headerRow.eachCell({ includeEmpty: false }, (cell) => {
    const label = normalizeHeaderLabel(cell.value);
    if (!COMMENT_DESCRIPTION_RENAME_FROM.has(label)) return;
    cell.value =
      label === "وصف التعليق"
        ? COMMENT_DESCRIPTION_HEADER_LABEL_AR
        : COMMENT_DESCRIPTION_HEADER_LABEL;
  });
};

const findLastImageColumn = (worksheet) => {
  const headerRow = worksheet.getRow(1);
  if (!headerRow) return null;

  let lastImageCol = null;
  headerRow.eachCell({ includeEmpty: false }, (cell, colNumber) => {
    if (/^image\s*[1-4]$/.test(normalizeHeaderLabel(cell.value))) {
      lastImageCol = Math.max(lastImageCol || 0, colNumber);
    }
  });
  return lastImageCol;
};

const applyCommentDescriptionColumnWidth = (worksheet, colNumber) => {
  if (!colNumber) return;
  const column = worksheet.getColumn(colNumber);
  column.width = COMMENT_DESCRIPTION_COLUMN_WIDTH;
  column.eachCell({ includeEmpty: true }, (cell) => {
    cell.alignment = {
      ...(cell.alignment || {}),
      wrapText: true,
      vertical: "top",
    };
  });
};

/**
 * Move Comment Description after Image 1–4 and shrink its width.
 * ExcelJS splice does not move floating images — keep anchors in sync.
 */
const placeCommentDescriptionAfterImages = (worksheet) => {
  const commentCol = findCommentDescriptionColumn(worksheet);
  if (!commentCol) return;

  const lastImageCol = findLastImageColumn(worksheet);
  if (!lastImageCol || commentCol > lastImageCol) {
    applyCommentDescriptionColumnWidth(worksheet, commentCol);
    return;
  }

  const columnValues = [];
  worksheet.eachRow((row, rowNumber) => {
    columnValues[rowNumber - 1] = row.getCell(commentCol).value;
  });

  worksheet.spliceColumns(commentCol, 1);
  shiftFloatingImagesLeft(worksheet, commentCol);

  const insertAt = lastImageCol; // after removal, former last image is at lastImageCol - 1; insert after it => lastImageCol
  worksheet.spliceColumns(insertAt, 0, columnValues);
  // Insert is after images — no floating-image shift needed.

  applyCommentDescriptionColumnWidth(worksheet, insertAt);
};

/** Prefer `Content-Disposition` filename from the export response. */
export const parseContentDispositionFilename = (header) => {
  if (!header || typeof header !== "string") return null;
  const utfMatch = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utfMatch?.[1]) {
    try {
      return decodeURIComponent(utfMatch[1].trim());
    } catch (_err) {
      return utfMatch[1].trim();
    }
  }
  const match = /filename="?([^";]+)"?/i.exec(header);
  return match?.[1]?.trim() || null;
};

/** Replace API `N/A` placeholders with `-`, strip unwanted columns, dedupe rows. */
export const normalizeExportWorkbookBlob = async (blob) => {
  const arrayBuffer = await blob.arrayBuffer();
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(arrayBuffer);

  workbook.eachSheet((worksheet) => {
    worksheet.eachRow((row) => {
      row.eachCell({ includeEmpty: true }, (cell) => {
        if (cell.value != null && typeof cell.value === "object") {
          if (typeof cell.value.text === "string") {
            cell.value = {
              ...cell.value,
              text: normalizeEmptyExportCell(cell.value.text),
            };
          }
          return;
        }
        cell.value = normalizeEmptyExportCell(cell.value);
      });
    });
    stripUnwantedExportColumns(worksheet);
    realignExportImageAnchors(worksheet);
    placeCommentDescriptionAfterImages(worksheet);
    renameCommentDescriptionHeader(worksheet);
    dedupeIdenticalDataRows(worksheet);
  });

  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};

export const triggerBlobDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(url);
};

/** Resolve JSON error message when Axios returns a blob error body. */
export const readBlobErrorMessage = async (data) => {
  if (!(typeof Blob !== "undefined" && data instanceof Blob)) return null;
  try {
    const text = await data.text();
    return JSON.parse(text)?.message || null;
  } catch (_err) {
    return null;
  }
};
